/**
 * Tipos para vistas de lista estándar (metadata-driven / View Factory).
 * Ver docs/BLUEPRINT-ARQUITECTURA.md y docs/guides/ESTRATEGIA-VISTAS-ESTANDAR-ODEO.md.
 *
 * Uso con ListView: las páginas que usan ListView deben cumplir la plantilla obligatoria;
 * ver docs/guides/LISTA-ESTANDAR-PLANTILLA.md → "Checklist obligatorio: plantilla ListView completa".
 */

import type { ReactNode } from 'react';
import type { IconSource as PolarisIconType } from '@/lib/polaris';

/**
 * Contenido opcional para celda expandible (popover al clic). Tipo Shopify: hover + chevron + desplegable con detalle.
 * @param item - Fila actual como objeto clave-valor; tipar según el recurso en la config de la lista.
 */
export type ExpandableContentRenderer = (item: Record<string, unknown>) => ReactNode;

/** Definición de una columna según su tipo */
export type ListViewColumn =
  | ListViewColumnThumbnail
  | ListViewColumnThumbnailWithText
  | ListViewColumnAvatarWithText
  | ListViewColumnText
  | ListViewColumnNumber
  | ListViewColumnPrice
  | ListViewColumnBadge
  | ListViewColumnCustom
  | ListViewColumnActions;

export interface ListViewColumnThumbnail {
  key: string;
  title: string;
  type: 'thumbnail';
  /** Key del campo con la URL de la imagen */
  imageKey?: string;
  /** Key del campo para contenido cuando no hay imagen (ej. icono emoji) */
  emptyKey?: string;
  altKey?: string;
  /** Si se define, la celda es expandible: hover + chevron + popover con este contenido al clic (estilo Shopify). */
  expandableContent?: ExpandableContentRenderer;
}

export interface ListViewColumnThumbnailWithText {
  key: string;
  title: string;
  type: 'thumbnailWithText';
  /** Key de la URL de la imagen */
  imageKey?: string;
  /** Key del texto junto a la imagen (ej. name) */
  textKey?: string;
  altKey?: string;
  fontWeight?: 'medium' | 'bold';
  /** Si se define, la celda es expandible: hover + chevron + popover con este contenido al clic (estilo Shopify). */
  expandableContent?: ExpandableContentRenderer;
}

/**
 * Avatar circular (Polaris Avatar) + texto (+ subtítulo opcional).
 *
 * Usar cuando:
 *   - El recurso es una persona (agente, usuario, cliente) o una entidad
 *     conceptual (equipo, etiqueta) — NO un archivo/producto con imagen real.
 *   - Sin imagen: se muestran iniciales generadas del `textKey` con color
 *     determinístico basado en el nombre.
 *   - Con imagen (`imageKey`): se muestra la foto en círculo (Polaris Avatar
 *     maneja la caída a iniciales si la URL falla).
 *
 * Diferencia con `thumbnailWithText`: aquel es rectangular con placeholder
 * de icono de imagen (products, invoices); este es circular con iniciales
 * (people, teams, labels).
 */
export interface ListViewColumnAvatarWithText {
  key: string;
  title: string;
  type: 'avatarWithText';
  /** Key del campo con URL de imagen (opcional). Si no se define o es null,
   *  se muestran iniciales. */
  imageKey?: string;
  /** Key del campo con el texto principal (ej. 'name'). Default: el `key` de la columna. */
  textKey?: string;
  /** Key del campo con subtítulo opcional (ej. 'email', 'description'). */
  subtitleKey?: string;
  /** Key del alt text para accesibilidad de la imagen. Default: usa `textKey`. */
  altKey?: string;
  fontWeight?: 'medium' | 'bold';
  /** Si se define, la celda es expandible: hover + chevron + popover con este contenido al clic. */
  expandableContent?: ExpandableContentRenderer;
}

export interface ListViewColumnText {
  key: string;
  title: string;
  type: 'text';
  fontWeight?: 'medium' | 'bold';
  tone?: 'subdued';
  /** Ancho máximo en px; si se define, el texto se trunca con ellipsis (...) */
  maxWidth?: number;
  /** Si se define, la celda es expandible: hover + chevron + popover con este contenido al clic (estilo Shopify). */
  expandableContent?: ExpandableContentRenderer;
}

export interface ListViewColumnNumber {
  key: string;
  title: string;
  type: 'number';
  suffix?: string;
  /** Ej: (n) => n === 1 ? 'producto' : 'productos' */
  format?: (value: number, item: Record<string, unknown>) => string;
  /** Tone del texto según valor (ej. stock ≤5 critical, ≤10 caution) */
  toneFromValue?: (value: number, item: Record<string, unknown>) => 'critical' | 'caution' | 'subdued' | undefined;
  /** Si se define, la celda es expandible: hover + chevron + popover con este contenido al clic (estilo Shopify). */
  expandableContent?: ExpandableContentRenderer;
}

/**
 * Columna de precio (dinero). Muestra el valor como `S/ 149.00`. Si el valor es null/vacío (ej. un
 * producto sin precio de comparación), muestra `—` en vez de `S/ 0.00`.
 */
export interface ListViewColumnPrice {
  key: string;
  title: string;
  type: 'price';
  /** Prefijo de moneda. Perú = 'S/ ' (default). */
  currencyPrefix?: string;
  /** Peso de la fuente. Default 'medium' (el precio de venta); usar 'regular' para columnas secundarias. */
  fontWeight?: 'regular' | 'medium';
  /** Tono del texto (ej. 'subdued' para el precio de comparación, que es secundario). */
  tone?: 'subdued';
  /** Si se define, la celda es expandible: hover + chevron + popover con este contenido al clic. */
  expandableContent?: ExpandableContentRenderer;
}

export interface ListViewColumnBadge {
  key: string;
  title: string;
  type: 'badge';
  /** Mapa valor -> etiqueta. Ej: { true: 'Activa', false: 'Inactiva' }. No requerido si se usa format.
   *  Las claves booleanas de un objeto literal se coercen a 'true'/'false' (string) en runtime. */
  map?: Record<string, string>;
  /** Formato dinámico del label (ej. moneda: (v) => `+ S/ ${Number(v).toFixed(2)}`) */
  format?: (value: unknown, item: Record<string, unknown>) => string;
  /** Key del campo para tone por fila */
  toneKey?: string;
  /** Tone según valor (ej. >=0 success, <0 attention). Subconjunto de tonos de Badge de Polaris. */
  toneFromValue?: (value: unknown) => 'success' | 'attention' | 'subdued' | 'critical' | 'info' | 'warning';
  /** Si se define, la celda es expandible: hover + chevron + popover con este contenido al clic (estilo Shopify). */
  expandableContent?: ExpandableContentRenderer;
}

/**
 * Tonos estándar para badge de estado Activo/Inactivo en listas.
 * Activo = verde (success), Inactivo = gris (subdued). Usar en columnas is_active/estado.
 */
export function getStatusBadgeTone(value: unknown): 'success' | 'subdued' {
  return value === true ? 'success' : 'subdued';
}

export interface ListViewColumnActions {
  key: string;
  title: string;
  type: 'actions';
  /** Ruta con :id para editar. Si no se define, no se muestra botón Editar */
  editRoute?: string;
  editLabel?: string;
  deleteLabel?: string;
  /** Si true, se muestra botón eliminar y se llama onRowDelete */
  delete?: boolean;
}

/**
 * Columna custom: el consumidor provee la función `render` que devuelve el
 * nodo a pintar en la celda. Útil para casos que no encajan en los tipos
 * predefinidos (ej. avatar con dot de disponibilidad, celdas con layout
 * compuesto, gráficos inline).
 *
 * Usar con moderación: para casos comunes preferir los tipos específicos
 * (`thumbnailWithText`, `badge`, etc.), que traen comportamiento uniforme
 * en toda la app.
 */
export interface ListViewColumnCustom<Item extends object = Record<string, unknown>> {
  key: string;
  title: string;
  type: 'custom';
  /** Función que recibe la fila completa y devuelve el nodo a renderizar. */
  render: (item: Item) => ReactNode;
  /** Ancho máximo; si se define, la celda trunca con ellipsis. */
  maxWidth?: number;
  /** Si se define, la celda es expandible: hover + chevron + popover con este contenido al clic. */
  expandableContent?: ExpandableContentRenderer;
}

/** Configuración del empty state */
export interface ListViewEmptyState {
  heading: string;
  description?: string;
  image?: string;
  action?: {
    content: string;
    to?: string;
    onAction?: () => void;
  };
  secondaryAction?: {
    content: string;
    onAction?: () => void;
  };
}

/** Acción bulk (ej. eliminar seleccionados) */
export interface ListViewBulkAction {
  content: string;
  /** Identificador para que el padre sepa qué hacer (ej. 'delete') */
  actionKey: string;
  /** Si true, se pinta en rojo (acción destructiva). Default: false. */
  destructive?: boolean;
}

/**
 * Borrado masivo genérico de la plantilla de listas.
 *
 * Si una página lo pasa a ListView/StandardListView (y `can !== false`), la plantilla
 * inyecta por sí sola una acción bulk destructiva "Eliminar" que:
 *   1. abre un AppConfirmModal con el conteo y el nombre del recurso,
 *   2. al confirmar llama `onDelete(ids)` — la página aporta el borrado REAL, tenant-safe
 *      (RPC con guard, o query con `.eq('tenant_id', …)`), porque el borrado es propio de
 *      cada entidad (cascada, soft-delete, correlativos): la plantilla NO adivina la tabla,
 *   3. muestra toast y limpia la selección.
 *
 * Así toda lista hereda la MISMA UX de borrado (modal, estilo destructivo, toast) declarando
 * ~3 líneas, sin recablear modales a mano. El permiso lo decide la página vía `can`
 * (no hay RBAC transversal: cada módulo calcula su propio permiso).
 */
export interface ListViewBulkDelete {
  /** Borrado real de la entidad (tenant-safe). Devuelve cuántos borró (o void). */
  onDelete: (ids: string[]) => Promise<number | void>;
  /** Gate de permiso. Si es false, la acción no se muestra. Default: true. */
  can?: boolean;
  /** Label de la acción/botón. Default: 'Eliminar'. */
  label?: string;
  /** Título del modal de confirmación. Default: `Eliminar N {recurso}`. */
  confirmTitle?: string;
  /**
   * Qué se pierde de verdad al borrar ESTOS registros. Se consulta al abrir el modal.
   *
   * POR QUÉ: "Esta acción no se puede deshacer" es cierto y no dice nada. Borrar un producto
   * arrastra su historial de inventario y el vínculo con los marketplaces; borrar una etiqueta no
   * arrastra nada. La misma advertencia para los dos entrena al vendedor a ignorarla.
   *
   * Devolver `requireConfirmText` hace que el modal pida escribirlo antes de habilitar el botón: la
   * fricción sigue a la consecuencia, en vez de ser igual para todo.
   *
   * Es opcional: sin esto, el modal se comporta exactamente como antes.
   */
  consequences?: (ids: string[]) => Promise<{
    /** Reemplaza "Esta acción no se puede deshacer" por algo que sí informe. */
    description?: ReactNode;
    /** Si viene, el vendedor debe escribirlo textual para poder confirmar. */
    requireConfirmText?: string;
  }>;
}

/**
 * Configuración completa de una vista de lista.
 * @template Item - Tipo de cada fila; debe ser un objeto con keys accesibles (p. ej. { id: string; name: string }).
 * Por defecto Record<string, unknown>; especificar Item mejora el tipado en config.columns y callbacks.
 */
export interface ListViewConfig<Item extends object = Record<string, unknown>> {
  /** Título de la página */
  title: string;
  /** Icono para breadcrumb (opcional) */
  titleIcon?: PolarisIconType;
  /** Si se define, se muestra botón "volver" con esta acción */
  onBack?: () => void;
  backAccessibilityLabel?: string;
  /**
   * Acción primaria del Page (botón esquina superior derecha).
   * Opcional: en listas sin "agregar" (ej. inventario) no se define.
   * Regla de la plantilla: si no se define content, la plantilla usará "Agregar {resourceName.singular}".
   */
  primaryAction?: {
    /** Si no se define, la plantilla usa "Agregar {resourceName.singular}". Estandarizar siempre en "Agregar X". */
    content?: string;
    to?: string;
    onAction?: () => void;
    icon?: PolarisIconType;
    /** Si true, se muestra el icono junto al texto. La plantilla siempre muestra el texto "Agregar {singular}" (nunca botón vacío). */
    iconOnly?: boolean;
  };
  /** Nombre del recurso para IndexTable (singular/plural). Usado también para el texto por defecto del botón primario. */
  resourceName: {
    singular: string;
    plural: string;
  };
  /** Definición de columnas (orden = orden en tabla) */
  columns: ListViewColumn[];
  /** Columnas ocultas por defecto (cuando no hay preferencias guardadas). Ej: ['phone', 'address'] */
  defaultHiddenColumnKeys?: string[];
  /** Empty state cuando no hay items */
  emptyState: ListViewEmptyState;
  /** Acciones bulk (ej. eliminar seleccionados) */
  bulkActions?: ListViewBulkAction[];
  /** Key del campo que identifica el item (default: 'id') */
  idKey?: keyof Item | string;
}
